// ABOUTME: Decides how the second step of a configuration chain opens after the first is finalized.
// ABOUTME: Keeps an already-configured step when the chain still resolves to it, seeds a fresh one otherwise.

import type { TUserAssignment } from '@coolshop/cool-configurator';

import type { ChainResolution } from '../hooks/useResolveChain';
import { ChainStep, parseLineItemConfiguration } from '../types';

export interface ChainStepSeed {
    /**
     * `keep` — the chain still resolves to the step already stored, so its selections are reused.
     * `seed` — the step opens from the carried-over selections only; any stored step is discarded,
     * which the caller confirms with the seller first.
     */
    action: 'keep' | 'seed';
    prototypeId: string;
    userAssignments: TUserAssignment[];
}

/**
 * Merges the selections carried over by the chain into the ones already made on the step, letting
 * the carry-over win per feature — the same by-featureCode override the configurator drawer applies
 * to the seller's defaults.
 */
function overlay(existing: TUserAssignment[], carriedOver: TUserAssignment[]): TUserAssignment[] {
    const overridden = new Set(carriedOver.map(assignment => assignment.featureCode));
    return [
        ...existing.filter(assignment => !overridden.has(assignment.featureCode)),
        ...carriedOver,
    ];
}

/**
 * Given a fresh chain resolution and the chain's currently persisted second step, decides what the
 * second configurator opens on.
 *
 * The comparison is against `prototypeId`, not `productId`: `nextProductId` is the prototype node
 * id the second configurator is opened with.
 */
export function resolveChainStepSeed(
    resolution: ChainResolution,
    existingStep?: ChainStep,
): ChainStepSeed {
    if (!existingStep || existingStep.prototypeId !== resolution.nextProductId) {
        return {
            action: 'seed',
            prototypeId: resolution.nextProductId,
            userAssignments: resolution.initialUserAssignments,
        };
    }

    const existingAssignments =
        parseLineItemConfiguration(existingStep.configurationJson)?.userAssignments ?? [];
    return {
        action: 'keep',
        prototypeId: existingStep.prototypeId,
        userAssignments: overlay(existingAssignments, resolution.initialUserAssignments),
    };
}
